import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { WorldProvider, useWorld } from "./state/World";
import CloudHold from "./components/CloudHold";
import HudDock from "./components/HudDock";
import Threshold from "./pages/Threshold";
import Atlas from "./pages/Atlas";
import Arcade from "./pages/Arcade";
import Cove from "./pages/Cove";
import PlayState from "./pages/PlayState";
import CoatCloset from "./pages/CoatCloset";
import StagewardWalk from "./pages/StagewardWalk";
import DrawingWalk from "./pages/DrawingWalk";
import Monorail from "./pages/Monorail";
import Table from "./pages/Table";
import Desk from "./pages/Desk";
import Proof from "./pages/Proof";
import MissionPage from "./pages/MissionPage";
import LedgerPage from "./pages/LedgerPage";
import Journal from "./pages/Journal";
import Admin from "./pages/Admin";
import CardPage from "./pages/CardPage";
import Commons from "./pages/Commons";
import Claim from "./pages/Claim";
import DocsOverlay from "./components/DocsOverlay";

const World = () => {
  const { me, loading, portal, portals } = useWorld();
  const nav = useNavigate();
  const location = useLocation();

  // Each Portal opens on its own front door. Which Portal we are in comes from
  // the registry the substrate serves, so a new Portal needs no code change here.
  useEffect(() => {
    if (!portal || portal === "endeavorum") return;
    const entry = portals.find((p) => p.key === portal)?.entry_path;
    if (entry && location.pathname === "/") {
      // Must go through the router, not window.history. replaceState changes the
      // address bar WITHOUT telling React Router, so the URL read
      // playforward.app/playforward while the router still rendered "/" — the
      // Atlas — which is the exact "the World is showing on the .app domain"
      // symptom. Keep the query string: campaign tags, ?nointro and ?portal all
      // have to survive the rewrite off a Practice apex's root.
      nav(entry + location.search, { replace: true });
    }
  }, [portal, portals, location.pathname, location.search, nav]);

  // Weather, not a spinner on black. See CloudHold.
  if (loading) return <CloudHold />;

  // A Nudger arrives with no session at all. The Challenge must open for them.
  const cardRoute = <Route path="/card/:cardId" element={<CardPage />} />;

  if (!me) {
    // FOUNDER'S RULE (11 Sep 2026): the .com landing NEVER shows the
    // Threshold box. If the Judge auto-boot is still in flight OR just
    // failed, the visitor should see weather (CloudHold), not a sign-in
    // panel over the World. Threshold is the "arrive as yourself" page —
    // it lives at /enter and nowhere else. /card/:id is the Nudger flow.
    // Any other path with no session shows the sky until boot resolves.
    return (
      <Routes>
        {cardRoute}
        <Route path="/enter" element={<Threshold />} />
        <Route path="/claim" element={<Claim />} />
        <Route path="*" element={<CloudHold />} />
      </Routes>
    );
  }

  return (
    <>
      {/* On the Atlas ('/'), the HUD is rendered inline by Atlas.jsx between
          the World and the Judges section so it scrolls with the page rather
          than floating over the map. Every other route keeps the fixed dock. */}
      {location.pathname !== "/" && <HudDock />}
      <AnimatePresence mode="wait">
        <Routes>
          {cardRoute}
          <Route path="/" element={<Atlas />} />
          <Route path="/enter" element={<Threshold />} />
          <Route path="/playforward" element={<Arcade />} />
          <Route path="/playforward/cove" element={<Cove />} />
          <Route path="/playforward/play" element={<PlayState />} />
          <Route path="/tour" element={<Monorail />} />
          <Route path="/stageward/walk" element={<StagewardWalk />} />
          <Route path="/stageward/drawing" element={<DrawingWalk />} />
          <Route path="/stageward" element={<CoatCloset />} />
          <Route path="/stageward/table" element={<Table />} />
          <Route path="/stageward/desk" element={<Desk />} />
          <Route path="/stageward/mission" element={<MissionPage />} />
          <Route path="/commons" element={<Commons />} />
          <Route path="/claim" element={<Claim />} />
          <Route path="/ledger" element={<LedgerPage />} />
          <Route path="/proof" element={<Proof />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/founder" element={<Admin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
      {/* Founder-only guidebook. Activated by ?docs=1 in any URL. Silent
          no-op for everyone else. */}
      <DocsOverlay />
    </>
  );
};

export default function App() {
  return (
    <WorldProvider>
      <BrowserRouter>
        <World />
        <Toaster
          theme="dark"
          position="bottom-center"
          toastOptions={{
            style: {
              background: "rgba(12,10,8,0.92)",
              border: "1px solid rgba(224,164,74,0.22)",
              color: "#f2e7d3",
              backdropFilter: "blur(14px)",
              fontFamily: "Outfit, sans-serif",
              fontSize: "13px",
            },
          }}
        />
      </BrowserRouter>
    </WorldProvider>
  );
}
